import type { FC } from 'react';

import { Link } from '@coop/components';

import type { SearchResultType } from '../../models/SearchResultType';

import ForceSoftNavigation from '../../../common/components/ForceSoftNavigation';
import tracking from '../../../common/tracking/tracking';
import {
    getFormattedSearchResultType,
    getFullSearchResultAbsoluteLink,
    getFullSearchResultLink,
    getFullSearchResultUrlWithOrder,
} from '../../utils/searchResultUtils';

export const SearchResultLink: FC<
    React.PropsWithChildren<{
        resultType?: SearchResultType;
        searchTerm: string;
        originalSearchTerm?: string;
        additionalClasses?: string;
        onPress?: () => void;
        'aria-label'?: string;
    }>
> = ({
    resultType,
    searchTerm,
    originalSearchTerm,
    children,
    additionalClasses,
    onPress,
    'aria-label': ariaLabel,
}) => {
    const linkData = getFullSearchResultLink(resultType, searchTerm, originalSearchTerm);
    if (!linkData.path) return null;

    return (
        <SearchBaseLink
            path={linkData.path}
            linkType={linkData.type}
            additionalClasses={additionalClasses}
            onPress={() => {
                const formatted = getFormattedSearchResultType(resultType);
                if (formatted) {
                    tracking.search.resultsPage.navigation(formatted);
                }

                onPress?.();
            }}
            aria-label={ariaLabel}
        >
            {children}
        </SearchBaseLink>
    );
};

export const SearchResultAbsoluteLink: FC<
    React.PropsWithChildren<{
        resultType?: SearchResultType;
        searchTerm?: string;
        originalSearchTerm?: string;
        additionalClasses?: string;
        onPress?: () => void;
        'aria-label'?: string;
    }>
> = ({
    resultType,
    searchTerm,
    originalSearchTerm,
    children,
    additionalClasses,
    onPress,
    'aria-label': ariaLabel,
}) => {
    if (!searchTerm) return null;

    const linkData = getFullSearchResultAbsoluteLink(resultType, searchTerm, originalSearchTerm);

    if (!linkData.path) return null;

    return (
        <SearchBaseLink
            path={linkData.path}
            linkType={linkData.type}
            additionalClasses={additionalClasses}
            onPress={onPress}
            aria-label={ariaLabel}
        >
            {children}
        </SearchBaseLink>
    );
};

/**
 * Forces all links to Search Results to be relative. Assumes that the componenyt where it is used can use SPA-only navigtaion, i.e. ecommerce search bar overlay.
 */
export const SearchResultRelativeLink: FC<
    React.PropsWithChildren<{
        resultType?: SearchResultType;
        searchTerm?: string;
        originalSearchTerm?: string;
        additionalClasses?: string;
        onPress?: () => void;
        'aria-label'?: string;
    }>
> = ({
    resultType,
    searchTerm,
    originalSearchTerm,
    children,
    additionalClasses,
    onPress,
    'aria-label': ariaLabel,
}) => {
    if (!searchTerm) return null;

    const linkData = getFullSearchResultAbsoluteLink(resultType, searchTerm, originalSearchTerm);

    if (!linkData.path) return null;
    const patchedLinkData = { ...linkData, type: 'relative' as const };

    return (
        <SearchBaseLink
            path={patchedLinkData.path}
            linkType={patchedLinkData.type}
            additionalClasses={additionalClasses}
            onPress={onPress}
            aria-label={ariaLabel}
        >
            {children}
        </SearchBaseLink>
    );
};

export const SearchAllResultWithOrderLink: FC<
    React.PropsWithChildren<{
        firstInOrder?: SearchResultType;
        searchTerm: string;
        originalSearchTerm?: string;
        additionalClasses?: string;
        onPress?: () => void;
        'aria-label'?: string;
    }>
> = ({
    firstInOrder,
    searchTerm,
    originalSearchTerm,
    children,
    additionalClasses,
    onPress,
    'aria-label': ariaLabel,
}) => {
    const linkData = getFullSearchResultUrlWithOrder(
        searchTerm,
        originalSearchTerm,
        firstInOrder || undefined,
    );

    if (!linkData.path) return null;

    return (
        <SearchBaseLink
            path={linkData.path}
            linkType={linkData.type}
            additionalClasses={additionalClasses}
            onPress={onPress}
            aria-label={ariaLabel}
        >
            {children}
        </SearchBaseLink>
    );
};
const SearchBaseLink: FC<
    React.PropsWithChildren<{
        path: string;
        linkType: 'absolute' | 'relative';
        additionalClasses?: string;
        onPress?: () => void;
        'aria-label'?: string;
    }>
> = ({ path, linkType, children, additionalClasses, onPress, 'aria-label': ariaLabel }) => {
    if (linkType === 'absolute') {
        return (
            <Link
                href={path}
                size="inline"
                className={additionalClasses}
                aria-label={ariaLabel}
                onPress={onPress}
            >
                {children}
            </Link>
        );
    }

    return (
        <ForceSoftNavigation>
            <Link
                size="inline"
                href={path}
                className={additionalClasses}
                aria-label={ariaLabel}
                onPress={onPress}
            >
                {children}
            </Link>
        </ForceSoftNavigation>
    );
};
